import { createSlice } from '@reduxjs/toolkit';
import * as _api from '../_apiHelpers/ReportsApihelper';
import { updateSnackbar } from '../SnackbarSlice';
import { updateLoader } from '../LoaderSlice';

// Initial state
const initialState = {
  data: null,
  code: null,
  message: '',
  isLoading: false,
};

// Create Custom Report Slice
export const createCustomReportSlice = createSlice({
  name: 'CreateCustomReport',
  initialState,
  reducers: {
    createCustomReportStartReducer: (state, action) => {
      return { ...state, ...action.payload };
    },
    createCustomReportReducer: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const { createCustomReportStartReducer, createCustomReportReducer } =
  createCustomReportSlice.actions;

export const createCustomReportAsync = (data) => async (dispatch) => {
  dispatch(updateLoader(true));
  dispatch(
    createCustomReportStartReducer({ isLoading: true, data: null, code: null })
  );

  try {
    const response = await _api.CreateCustomReport(data);

    if (response && response.status === 200) {
      dispatch(
        createCustomReportReducer({
          data: response.data,
          code: response.status,
          message: 'Report request submitted successfully',
          isLoading: false,
        })
      );
      dispatch(
        updateSnackbar({
          type: 'success',
          message: 'Report request submitted successfully',
        })
      );
    } else if (response && response.status === 201) {
      dispatch(
        createCustomReportReducer({
          data: response.data,
          code: response.status,
          message: 'Report created successfully',
          isLoading: false,
        })
      );
      dispatch(
        updateSnackbar({
          type: 'success',
          message: 'Report created successfully',
        })
      );
    } else {
      dispatch(
        createCustomReportReducer({
          data: null,
          code: response?.status || 500,
          message: response?.data?.message || 'Failed to create report',
          isLoading: false,
        })
      );
      dispatch(
        updateSnackbar({
          type: 'error',
          message: response?.data?.message || 'Failed to create report',
        })
      );
    }
  } catch (error) {
    dispatch(
      createCustomReportReducer({
        data: null,
        code: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          'An error occurred while creating report',
        isLoading: false,
      })
    );
    dispatch(
      updateSnackbar({
        type: 'error',
        message:
          error.response?.data?.message ||
          'An error occurred while creating report',
      })
    );
  } finally {
    dispatch(updateLoader(false));
  }
};

// Get Custom Reports Slice
export const getCustomReportsSlice = createSlice({
  name: 'GetCustomReports',
  initialState,
  reducers: {
    getCustomReportsStartReducer: (state, action) => {
      return { ...state, ...action.payload };
    },
    getCustomReportsReducer: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const { getCustomReportsStartReducer, getCustomReportsReducer } =
  getCustomReportsSlice.actions;

export const getCustomReportsAsync =
  (createdBy, page = 1, limit = 50) =>
  async (dispatch) => {
    dispatch(updateLoader(true));
    dispatch(
      getCustomReportsStartReducer({ isLoading: true, data: null, code: null })
    );

    try {
      const response = await _api.GetCustomReports(createdBy, page, limit);

      if (response && (response.status === 200 || response.status === 201)) {
        dispatch(
          getCustomReportsReducer({
            data: response.data,
            code: response.status,
            message: 'Reports fetched successfully',
            isLoading: false,
          })
        );
      } else {
        dispatch(
          getCustomReportsReducer({
            data: null,
            code: response?.status || 500,
            message: response?.data?.message || 'Failed to fetch reports',
            isLoading: false,
          })
        );
      }
    } catch (error) {
      dispatch(
        getCustomReportsReducer({
          data: null,
          code: error.response?.status || 500,
          message:
            error.response?.data?.message ||
            'An error occurred while fetching reports',
          isLoading: false,
        })
      );
    } finally {
      dispatch(updateLoader(false));
    }
  };

// Delete Custom Report Slice
export const deleteCustomReportSlice = createSlice({
  name: 'DeleteCustomReport',
  initialState,
  reducers: {
    deleteCustomReportStartReducer: (state, action) => {
      return { ...state, ...action.payload };
    },
    deleteCustomReportReducer: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const { deleteCustomReportStartReducer, deleteCustomReportReducer } =
  deleteCustomReportSlice.actions;

export const deleteCustomReportAsync = (id, createdBy) => async (dispatch) => {
  dispatch(updateLoader(true));
  dispatch(
    deleteCustomReportStartReducer({ isLoading: true, data: null, code: null })
  );

  try {
    const response = await _api.DeleteCustomReport(id, createdBy);

    if (response && (response.status === 200 || response.status === 204)) {
      dispatch(
        deleteCustomReportReducer({
          data: response.data,
          code: response.status,
          message: 'Report deleted successfully',
          isLoading: false,
        })
      );
      dispatch(
        updateSnackbar({
          type: 'success',
          message: 'Report deleted successfully',
        })
      );
      // Refresh the reports list after successful deletion
      dispatch(getCustomReportsAsync(createdBy));
    } else {
      dispatch(
        deleteCustomReportReducer({
          data: null,
          code: response?.status || 500,
          message: response?.data?.message || 'Failed to delete report',
          isLoading: false,
        })
      );
      dispatch(
        updateSnackbar({
          type: 'error',
          message: response?.data?.message || 'Failed to delete report',
        })
      );
    }
  } catch (error) {
    dispatch(
      deleteCustomReportReducer({
        data: null,
        code: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          'An error occurred while deleting report',
        isLoading: false,
      })
    );
    dispatch(
      updateSnackbar({
        type: 'error',
        message:
          error.response?.data?.message ||
          'An error occurred while deleting report',
      })
    );
  } finally {
    dispatch(updateLoader(false));
  }
};
